/**
 * Widget 顶部栏组件
 *
 * 设计规范（来自 pencil）：
 * - 高度：56px
 * - 背景：蓝色 (#2563EB)
 * - 左侧：客服头像 + 标题 + 副标题
 * - 右侧：新建对话按钮 + 历史记录按钮 + 关闭按钮
 * - 圆角：顶部 16px
 *
 * 可访问性：
 * - aria-label 标识客服状态
 * - 按钮有明确的 aria-label
 */

import React from 'react';
import { clsx } from 'clsx';

export interface WidgetHeaderProps {
  /** 标题 */
  title?: string;
  /** 副标题 */
  subtitle?: string;
  /** 是否连接 */
  isConnected?: boolean;
  /** 平均响应时间 */
  responseTime?: string;
  /** 关闭回调 */
  onClose: () => void;
  /** 新建对话回调 */
  onNewChat?: () => void;
  /** 打开历史记录回调 */
  onHistory?: () => void;
  /** 是否禁用新建对话按钮 */
  newChatDisabled?: boolean;
  /** 是否禁用历史记录按钮 */
  historyDisabled?: boolean;
  /** 自定义 CSS 类名 */
  className?: string;
}

export const WidgetHeader: React.FC<WidgetHeaderProps> = ({
  title = '智能客服',
  subtitle,
  isConnected = true,
  responseTime = '< 2s',
  onClose,
  onNewChat,
  onHistory,
  newChatDisabled = false,
  historyDisabled = false,
  className,
}) => {
  // 默认副标题：显示在线状态和响应时间
  const defaultSubtitle = isConnected
    ? `在线 · 平均响应 ${responseTime}`
    : '离线 · 请稍后尝试';

  return (
    <div
      className={clsx(
        'flex items-center justify-between gap-[12px] px-[16px] h-[56px]',
        'bg-[#2563EB]',
        'rounded-t-[16px]',
        className
      )}
      aria-label="客服对话框顶部栏"
    >
      {/* 左侧：头像 + 标题区 */}
      <div className="flex items-center gap-[12px] flex-1 min-w-0">
        {/* 客服头像 - 蓝色圆圈 */}
        <div
          className="flex-shrink-0 w-[32px] h-[32px] rounded-full bg-white/20 flex items-center justify-center"
          aria-hidden="true"
        >
          {/* 简单的机器人图标 */}
          <svg
            className="w-[20px] h-[20px] text-white"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
            />
          </svg>
        </div>

        {/* 标题区 */}
        <div className="flex flex-col gap-[2px] min-w-0">
          <h2 className="text-[14px] leading-[20px] font-semibold text-white truncate">
            {title}
          </h2>
          <p className="text-[12px] leading-[16px] text-white/80 truncate">
            {subtitle || defaultSubtitle}
          </p>
        </div>
      </div>

      {/* 右侧：按钮组 */}
      <div className="flex items-center gap-[4px] flex-shrink-0">
        {/* 新建对话按钮 */}
        {onNewChat && (
          <button
            type="button"
            onClick={onNewChat}
            disabled={newChatDisabled}
            className={clsx(
              'p-[6px] rounded-lg transition-all duration-200',
              'text-white/80 hover:text-white hover:bg-white/10',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50'
            )}
            aria-label="新建对话"
          >
            <svg className="w-[20px] h-[20px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
          </button>
        )}

        {/* 历史记录按钮 */}
        {onHistory && (
          <button
            type="button"
            onClick={onHistory}
            disabled={historyDisabled}
            className={clsx(
              'p-[6px] rounded-lg transition-all duration-200',
              'text-white/80 hover:text-white hover:bg-white/10',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50'
            )}
            aria-label="历史记录"
          >
            <svg className="w-[20px] h-[20px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </button>
        )}

        {/* 关闭按钮 */}
        <button
          type="button"
          onClick={onClose}
          className="flex-shrink-0 p-[6px] rounded-lg transition-all duration-200 text-white/80 hover:text-white hover:bg-white/10 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/50"
          aria-label="最小化对话框"
        >
          <svg className="w-[20px] h-[20px]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
          </svg>
        </button>
      </div>
    </div>
  );
};
